import type { SearchDomain } from '@shared/types';
import { dispatchShellDomainInvalidated, type ShellDomain } from '@renderer/app/events/shell-domain-events';
import { useShellStore } from '@renderer/app/state/shell-store';
import { getDesktopApi } from '@renderer/lib/desktop-api';
import { translate } from '@renderer/shared/i18n';
import { useUiStore } from '@renderer/stores/ui-store';

const shellDomains = new Set<SearchDomain>([
  'projects',
  'leads',
  'tasks',
  'customers',
  'resources',
  'contracts',
  'invoices',
  'payments'
]);

function getLocale() {
  return useShellStore.getState().settings.locale;
}

function t(key: string): string {
  return translate(getLocale(), key);
}

function invalidateDomains(domains: SearchDomain[]): void {
  for (const domain of domains) {
    if (shellDomains.has(domain)) {
      dispatchShellDomainInvalidated(domain as ShellDomain);
    }
  }
}

export async function runLastUndoFromRenderer(): Promise<void> {
  const addToast = useUiStore.getState().addToast;

  try {
    const result = await getDesktopApi().undo.undoLast();

    if (result.ok) {
      invalidateDomains(result.affectedDomains);
      addToast({
        title: t('undo.toast.undoneTitle'),
        message: t('undo.toast.undoneMessage'),
        tone: 'success'
      });
      return;
    }

    addToast({
      title: t('undo.toast.unavailableTitle'),
      message: result.message || t('undo.toast.unavailableMessage'),
      tone: 'warning'
    });
  } catch (error) {
    addToast({
      title: t('undo.toast.failedTitle'),
      message: error instanceof Error ? error.message : t('undo.toast.failedMessage'),
      tone: 'danger'
    });
  }
}

export async function showUndoToastForLastAction(): Promise<void> {
  try {
    const entry = await getDesktopApi().undo.getLastUndoable();

    if (!entry) {
      return;
    }

    useUiStore.getState().addToast({
      title: t('undo.toast.availableTitle'),
      message: t('undo.toast.availableMessage'),
      tone: 'neutral',
      action: {
        label: t('undo.actions.undo'),
        onClick: () => void runLastUndoFromRenderer()
      }
    });
  } catch {
    // Older test bridges or stale preload builds may not expose undo yet.
  }
}
